import { InView } from 'react-intersection-observer';
import { BookisState } from './bookis-state';
import style from './bookis.module.scss';
import Image from 'next/image';
import StartSection from './sections/start-section';
import ProjectInfoSection from './sections/project-info-section';
import AccordionSection from './sections/accordion-section';
import BenefitsSection from './sections/benefits-section';
import animations from '@styles/animations.module.scss';
import Breadcrumbs from '@components/shared/miscellaneous/breadcrumbs/breadcrumbs';

interface BookisComponentProps {
  breadcrumbsText: string;
}

const Bookis = ({ breadcrumbsText }: BookisComponentProps) => {
  return (
    <>
      <Breadcrumbs lastElementTitle={breadcrumbsText} />
      <div className={style.bookis}>
        <StartSection
          title={BookisState.startBlock.title}
          subtitle={BookisState.startBlock.subtitle}
          image={BookisState.startBlock.image}
          text={BookisState.startBlock.text}
        />
        <ProjectInfoSection title={BookisState.projectInfoBlock.title} points={BookisState.projectInfoBlock.points} />
        <AccordionSection
          title={BookisState.accordion.title}
          subtitle={BookisState.accordion.subtitle}
          items={BookisState.accordion.items}
        />
        <BenefitsSection
          title={BookisState.benefitsSeller.title}
          points={BookisState.benefitsSeller.points}
          colorGray={true}
        />
        <BenefitsSection
          title={BookisState.benefitsBuyer.title}
          points={BookisState.benefitsBuyer.points}
          colorGray={false}
        />
        <section className={`${style.section} ${style.section_gray}`}>
          <div className={`${style.container} ${style.preview}`}>
            <div className={style.preview__text}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={`${style.preview__title} ${animations.scroll__animation}`}>
                      {BookisState.previewBlocks[0].title}
                    </div>
                  );
                }}
              </InView>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div
                      ref={ref}
                      className={`${style.container__subtitle} ${style.container__subtitle_left} ${animations.scroll__animation}`}
                    >
                      {BookisState.previewBlocks[0].subtitle}
                    </div>
                  );
                }}
              </InView>
              <div>
                {BookisState.previewBlocks[0].points?.map((item: string, index: number) => {
                  return (
                    <InView key={index} triggerOnce={true}>
                      {({ inView, ref, entry }: any) => {
                        if (inView) {
                          entry?.target.classList.add(animations.active);
                        }
                        return (
                          <div
                            ref={ref}
                            className={`${style.container__points} ${style.container__list__points} ${animations.scroll__animation}`}
                          >
                            {item}
                          </div>
                        );
                      }}
                    </InView>
                  );
                })}
              </div>
            </div>

            <div className={`${style.preview__image}`}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={` ${style.preview__image} ${animations.scroll__animation}`}>
                      <Image
                        sizes="800px 200px"
                        layout="fill"
                        src={BookisState.previewBlocks[0].image}
                        alt={BookisState.previewBlocks[0].title}
                      />
                    </div>
                  );
                }}
              </InView>
            </div>
          </div>
        </section>
        <section className={`${style.section} ${style.section_light}`}>
          <div className={`${style.container} ${style.preview} ${style.preview_reversed}`}>
            <div className={style.preview__text}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={`${style.preview__title} ${animations.scroll__animation}`}>
                      {BookisState.previewBlocks[1].title}
                    </div>
                  );
                }}
              </InView>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div
                      ref={ref}
                      className={`${style.container__subtitle} ${style.container__subtitle_left} ${animations.scroll__animation}`}
                    >
                      {BookisState.previewBlocks[1].subtitle}
                    </div>
                  );
                }}
              </InView>
              <div>
                {BookisState.previewBlocks[1].points?.map((item: string, index: number) => {
                  return (
                    <InView key={index} triggerOnce={true}>
                      {({ inView, ref, entry }: any) => {
                        const lastIndex = BookisState.previewBlocks[1].points!.length - 1;
                        if (inView) {
                          entry?.target.classList.add(animations.active);
                        }
                        return (
                          <div
                            ref={ref}
                            className={`${lastIndex === index ? style.container__list__points_last : null} ${style.container__points} ${style.container__list__points} ${animations.scroll__animation}`}
                          >
                            {item}
                          </div>
                        );
                      }}
                    </InView>
                  );
                })}
                <InView triggerOnce={true}>
                  {({ inView, ref, entry }: any) => {
                    if (inView) {
                      entry?.target.classList.add(animations.active);
                    }
                    return (
                      <div
                        ref={ref}
                        className={`${style.container__subtitle} ${style.container__subtitle_left} ${animations.scroll__animation}`}
                      >
                        {BookisState.previewBlocks[1].text}
                      </div>
                    );
                  }}
                </InView>
              </div>
            </div>

            <div className={`${style.preview__image}`}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={` ${style.preview__image} ${animations.scroll__animation}`}>
                      <Image
                        sizes="800px 200px"
                        layout="fill"
                        src={BookisState.previewBlocks[1].image}
                        alt={BookisState.previewBlocks[1].title}
                      />
                    </div>
                  );
                }}
              </InView>
            </div>
          </div>
        </section>
        <section className={`${style.section} ${style.section_gray}`}>
          <div className={`${style.container} ${style.preview}`}>
            <div className={style.preview__text}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={`${style.preview__title} ${animations.scroll__animation}`}>
                      {BookisState.previewBlocks[2].title}
                    </div>
                  );
                }}
              </InView>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div
                      ref={ref}
                      className={`${style.container__subtitle} ${style.container__subtitle_left} ${animations.scroll__animation}`}
                    >
                      {BookisState.previewBlocks[2].subtitle}
                    </div>
                  );
                }}
              </InView>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div
                      ref={ref}
                      className={`${style.container__subtitle} ${style.container__subtitle_left} ${animations.scroll__animation}`}
                    >
                      {BookisState.previewBlocks[2].text}
                    </div>
                  );
                }}
              </InView>
            </div>

            <div className={`${style.preview__image}`}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={` ${style.preview__image} ${animations.scroll__animation}`}>
                      <Image
                        sizes="800px 200px"
                        layout="fill"
                        src={BookisState.previewBlocks[2].image}
                        alt={BookisState.previewBlocks[2].title}
                      />
                    </div>
                  );
                }}
              </InView>
            </div>
          </div>
        </section>
        <section className={`${style.section} ${style.section_light}`}>
          <div className={`${style.container} ${style.preview} ${style.preview_reversed}`}>
            <div className={style.preview__text}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={`${style.preview__title} ${animations.scroll__animation}`}>
                      {BookisState.previewBlocks[3].title}
                    </div>
                  );
                }}
              </InView>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div
                      ref={ref}
                      className={`${style.container__subtitle} ${style.container__subtitle_left} ${animations.scroll__animation}`}
                    >
                      {BookisState.previewBlocks[3].subtitle}
                    </div>
                  );
                }}
              </InView>
              <div>
                <InView triggerOnce={true}>
                  {({ inView, ref, entry }: any) => {
                    if (inView) {
                      entry?.target.classList.add(animations.active);
                    }
                    return (
                      <div
                        ref={ref}
                        className={`${style.container__subtitle} ${style.container__subtitle_left} ${animations.scroll__animation}`}
                      >
                        {BookisState.previewBlocks[3].text}
                      </div>
                    );
                  }}
                </InView>
                {BookisState.previewBlocks[3].points?.map((item: string, index: number) => {
                  return (
                    <InView key={index} triggerOnce={true}>
                      {({ inView, ref, entry }: any) => {
                        if (inView) {
                          entry?.target.classList.add(animations.active);
                        }
                        return (
                          <div
                            ref={ref}
                            className={`${style.container__points} ${style.container__list__points} ${animations.scroll__animation}`}
                          >
                            {item}
                          </div>
                        );
                      }}
                    </InView>
                  );
                })}
              </div>
            </div>

            <div className={`${style.preview__image}`}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={` ${style.preview__image} ${animations.scroll__animation}`}>
                      <Image
                        sizes="800px 200px"
                        layout="fill"
                        src={BookisState.previewBlocks[3].image}
                        alt={BookisState.previewBlocks[3].title}
                      />
                    </div>
                  );
                }}
              </InView>
            </div>
          </div>
        </section>
        <section className={`${style.section} ${style.section_gray}`}>
          <div className={`${style.container} ${style.preview}`}>
            <div className={style.preview__text}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={`${style.preview__title} ${animations.scroll__animation}`}>
                      {BookisState.previewBlocks[4].title}
                    </div>
                  );
                }}
              </InView>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div
                      ref={ref}
                      className={`${style.container__subtitle} ${style.container__subtitle_left} ${animations.scroll__animation}`}
                    >
                      {BookisState.previewBlocks[4].text}
                    </div>
                  );
                }}
              </InView>
            </div>

            <div className={`${style.preview__image}`}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={` ${style.preview__image} ${animations.scroll__animation}`}>
                      <Image
                        sizes="800px 200px"
                        layout="fill"
                        src={BookisState.previewBlocks[4].image}
                        alt={BookisState.previewBlocks[4].title}
                      />
                    </div>
                  );
                }}
              </InView>
            </div>
          </div>
        </section>
        <section className={`${style.section} ${style.section_light}`}>
          <div className={`${style.container} ${style.preview} ${style.preview_reversed}`}>
            <div className={style.preview__text}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={`${style.preview__title} ${animations.scroll__animation}`}>
                      {BookisState.previewBlocks[5].title}
                    </div>
                  );
                }}
              </InView>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div
                      ref={ref}
                      className={`${style.container__subtitle} ${style.container__subtitle_left} ${animations.scroll__animation}`}
                    >
                      {BookisState.previewBlocks[5].text}
                    </div>
                  );
                }}
              </InView>
            </div>

            <div className={`${style.preview__image}`}>
              <InView triggerOnce={true}>
                {({ inView, ref, entry }: any) => {
                  if (inView) {
                    entry?.target.classList.add(animations.active);
                  }
                  return (
                    <div ref={ref} className={` ${style.preview__image} ${animations.scroll__animation}`}>
                      <Image
                        sizes="800px 200px"
                        layout="fill"
                        src={BookisState.previewBlocks[5].image}
                        alt={BookisState.previewBlocks[5].title}
                      />
                    </div>
                  );
                }}
              </InView>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Bookis;
